import { useFlag } from '@calendly/feature-check-react';
import { Fieldset, FieldError, FieldLabel, useErrorId } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { Select, Control, Options, Option } from '@calendly/ui/components/select';
import classNames from 'classnames';
import { useCallback, useMemo, forwardRef } from 'react';
import { ENT_IT_WORKFLOW_ACTION_EMAIL_SOMEONE_ELSE } from 'constants/feature_flags';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { useIsSmsDeactivated } from 'features/organization_settings';
import { publisherWorkflowsT } from 'services/workflows/publisher_workflows_i18n';
const emailToInviteeType = 'booking.email.event_to_invitee';
const emailToPublisherType = 'booking.email.event_to_publisher';
const emailToSomeoneElseType = 'booking.email.event_to_someone_else';
const smsToInviteeType = 'booking.sms.event_to_invitee';
const smsToPublisherType = 'booking.sms.event_to_publisher';
const buildActionDetailsMap = wfT => ({
  [emailToInviteeType]: {
    title: () => wfT('publisher.workflows.actions.booking.email.event_to_invitee.title'),
    description: () => wfT('publisher.workflows.actions.booking.email.event_to_invitee.description')
  },
  [emailToSomeoneElseType]: {
    title: () => wfT('publisher.workflows.actions.booking.email.event_to_someone_else.title'),
    description: () => wfT('publisher.workflows.actions.booking.email.event_to_someone_else.description')
  },
  [emailToPublisherType]: {
    title: () => wfT('publisher.workflows.actions.booking.email.event_to_publisher.title'),
    description: () => wfT('publisher.workflows.actions.booking.email.event_to_publisher.description')
  },
  [smsToInviteeType]: {
    title: () => wfT('publisher.workflows.actions.booking.sms.event_to_invitee.title'),
    description: () => wfT('publisher.workflows.actions.booking.sms.event_to_invitee.description')
  },
  [smsToPublisherType]: {
    title: () => wfT('publisher.workflows.actions.booking.sms.event_to_publisher.title'),
    description: () => wfT('publisher.workflows.actions.booking.sms.event_to_publisher.description')
  }
});
const useWorkflowsModalTranslation = () => {
  const isAppShellRebrand = useProductRefreshAppShell();
  const wfT = useCallback((key, opts) => publisherWorkflowsT(key, isAppShellRebrand, opts), [isAppShellRebrand]);
  const actionDetailsMap = useMemo(() => buildActionDetailsMap(wfT), [wfT]);
  return {
    wfT,
    actionDetailsMap
  };
};
const actionTitleCss = "ax9t7mg";
const ActionTitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionTitleCss, props.className)} />;
});
const actionDescriptionCss = "a1xa85um";
const ActionDescription = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionDescriptionCss, props.className)} />;
});
const badgeCss = "b6gmbdq";
const Badge = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(badgeCss, props.className)} />;
});
const deactivatedBadgeCss = "d1p0xttk";
const DeactivatedBadge = forwardRef((props, ref) => <Badge ref={ref} {...props} className={classNames(deactivatedBadgeCss, props.className)}>
    {I18n.t('publisher.enforcement.sms_deactivated')}
  </Badge>);
const workflowsDeactivatedBadgeCss = "w16kc8ac";
const WorkflowsDeactivatedBadge = forwardRef((props, ref) => {
  const isAppShellRebrand = useProductRefreshAppShell();
  return <Badge ref={ref} {...props} className={classNames(workflowsDeactivatedBadgeCss, props.className)}>
      {publisherWorkflowsT('publisher.workflows.actions.one_time_action', isAppShellRebrand)}
    </Badge>;
});
const ActionDetails = ({
  type
}) => {
  const {
    actionDetailsMap
  } = useWorkflowsModalTranslation();
  return <>
      <ActionTitle>
        {actionDetailsMap[type].title()}
      </ActionTitle>
      <ActionDescription>
        {actionDetailsMap[type].description()}
      </ActionDescription>
    </>;
};
const EmailOption = ({
  type
}) => <Option value={type}>
    <ActionDetails type={type} />
  </Option>;
const DeactivatedOption = ({
  type,
  badge: BadgeComponent = DeactivatedBadge
}) => <Option value={type} disabled>
    <ActionDetails type={type} />
    <BadgeComponent />
  </Option>;
const EmailToSomeoneElseDeactivatedOption = () => <DeactivatedOption type={emailToSomeoneElseType} badge={WorkflowsDeactivatedBadge} />;
const SmsOption = ({
  type
}) => {
  const smsDeactivated = useIsSmsDeactivated();
  if (smsDeactivated) {
    return <DeactivatedOption type={type} />;
  }
  return <EmailOption type={type} />;
};
const EmailToInvitee = () => <EmailOption type={emailToInviteeType} />;
const EmailToSomeoneElse = ({
  isActionAlreadyAssociated
}) => isActionAlreadyAssociated ? <EmailToSomeoneElseDeactivatedOption /> : <EmailOption type={emailToSomeoneElseType} />;
const EmailToHost = () => <EmailOption type={emailToPublisherType} />;
const SmsToInvitee = () => <SmsOption type={smsToInviteeType} />;
const SmsToPublisher = () => <SmsOption type={smsToPublisherType} />;
const formFieldId = 'doThis';
const selectButtonId = 'doThisControl';
export const ActionSelect = ({
  visibleActions = [],
  value,
  onChange,
  disabled,
  invalid,
  error
}) => {
  const errorId = useErrorId({
    error
  });
  const {
    wfT,
    actionDetailsMap
  } = useWorkflowsModalTranslation();
  const workflowActionEmailSomeoneElseEnabled = useFlag(ENT_IT_WORKFLOW_ACTION_EMAIL_SOMEONE_ELSE);
  const isWorkflowActionEmailSomeoneElseVisible = useMemo(() => visibleActions.some(action => action.configuration.type === emailToSomeoneElseType), [visibleActions]);
  return <Fieldset>
      <FieldLabel id={formFieldId} text={wfT('publisher.workflows.create.do_this')} />
      <Select value={value} onChange={onChange}>
        <Control id={selectButtonId} aria-labelledby={formFieldId} aria-describedby={errorId} disabled={disabled} invalid={invalid} aria-invalid={invalid} placeholder={wfT('publisher.workflows.forms.select')}>
          {value && actionDetailsMap[value].title()}
        </Control>
        <Options>
          <EmailToInvitee />
          <EmailToHost />
          {workflowActionEmailSomeoneElseEnabled && <EmailToSomeoneElse isActionAlreadyAssociated={isWorkflowActionEmailSomeoneElseVisible} />}
          <SmsToInvitee />
          <SmsToPublisher />
        </Options>
      </Select>
      <FieldError text={error} id={errorId} />
    </Fieldset>;
};

require("./action_select.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./action_select.js");